import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-semibold ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shadow-primary/20',
        outline: 'border border-input bg-background hover:bg-secondary hover:text-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-secondary hover:text-foreground',
        link: 'text-[hsl(var(--accent-ink))] underline-offset-4 hover:underline',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // Vimeo-style chunky pill, full-rounded, lime ground, hover
        // darkens the arrow. Use with `<ButtonCta />` for the right-
        // hand circle-arrow lockup, or `<Button variant="cta">` alone
        // for a plain pill.
        cta: 'rounded-full bg-primary pl-5 pr-1.5 text-foreground hover:bg-primary/90 shadow-md shadow-primary/30 font-semibold',
        'cta-dark': 'rounded-full bg-foreground pl-5 pr-1.5 text-background hover:bg-foreground/90 shadow-md font-semibold',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-9 px-4',
        lg: 'h-12 px-6 text-base',
        // Vimeo-style pill is bigger and asymmetric (left padding for
        // label, right padding tight against the arrow circle).
        cta: 'h-12 text-[15px]',
        'cta-lg': 'h-14 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  },
)
Button.displayName = 'Button'

export { buttonVariants }
